/**
 * questionari/statistiche — i conti delle risposte, per la console e per il riepilogo dell'email.
 * ============================================================================
 *
 * Una persona conta UNA volta: solo le risposte `attuale` (le vecchie, sostituite da un nuovo invio
 * della stessa persona, e quelle ritirate da chi è uscito restano nell'archivio ma non nei conti).
 * Le risposte salvate prima del 18/10/2026 non hanno lo stato: valgono come attuali.
 *
 * Le etichette vengono dalla definizione di OGGI; se un'opzione è stata tolta, dall'istantanea
 * salvata con la risposta; se manca anche quella, dal codice stesso. Mai un conto perso.
 *
 * File puro, target ES2017.
 */

import { etichettaProvenienza } from './provenienza';
import type {
  Domanda,
  IstantaneaDomanda,
  Provenienza,
  RispostaSalvata,
  StatoRisposta,
  ValoreRisposta,
} from './tipi';

/** Ciò che serve di una risposta per contarla (la console la legge da Firestore). */
export interface RispostaPerStatistiche {
  id: string;
  stato?: StatoRisposta;
  risposte: RispostaSalvata[];
  provenienza?: Partial<Provenienza> | null;
  /** ISO 8601 o millisecondi. */
  completato_il: string | number;
  istantanea_domande?: IstantaneaDomanda[];
  consenso_richiesto?: boolean;
}

export function contaNeiConti(r: Pick<RispostaPerStatistiche, 'stato'>): boolean {
  return r.stato === undefined || r.stato === 'attuale';
}

export interface ConteggioOpzione {
  id: string;
  etichetta: string;
  conteggio: number;
  /** Sul totale di chi ha risposto a quella domanda (nelle multiple la somma supera 100). */
  percentuale: number;
}

export interface StatisticheNumero {
  risposte: number;
  somma: number;
  media: number;
  mediana: number;
  minimo: number;
  massimo: number;
  /** Fasce leggibili per il grafico (1, 2-5, 6-10, 11-20, 21-50, oltre 50). */
  fasce: Array<{ etichetta: string; conteggio: number }>;
}

export interface StatisticheDomanda {
  domanda_id: string;
  testo: string;
  nome_breve: string;
  tipo: Domanda['tipo'];
  /** Quante persone hanno risposto (le facoltative possono essere meno del totale). */
  risposte: number;
  opzioni?: ConteggioOpzione[];
  numero?: StatisticheNumero;
  /** Testi liberi e campi di dettaglio, dal più recente. */
  testi?: Array<{ response_id: string; testo: string }>;
}

export interface StatisticheQuestionario {
  totale: number;
  per_provenienza: Array<{ provenienza: string; conteggio: number }>;
  /** Giorno (AAAA-MM-GG, ora italiana non serve: il giorno UTC basta per l'andamento). */
  per_giorno: Array<{ giorno: string; conteggio: number }>;
  consenso_richiesto: number;
  domande: StatisticheDomanda[];
}

const FASCE_NUMERO: Array<{ etichetta: string; da: number; a: number }> = [
  { etichetta: '1', da: 1, a: 1 },
  { etichetta: '2-5', da: 2, a: 5 },
  { etichetta: '6-10', da: 6, a: 10 },
  { etichetta: '11-20', da: 11, a: 20 },
  { etichetta: '21-50', da: 21, a: 50 },
  { etichetta: 'oltre 50', da: 51, a: Number.POSITIVE_INFINITY },
];

function giornoDi(quando: string | number): string {
  const d = new Date(quando);
  return Number.isNaN(d.getTime()) ? 'sconosciuto' : d.toISOString().slice(0, 10);
}

function percentuale(parte: number, totale: number): number {
  return totale > 0 ? Math.round((parte / totale) * 1000) / 10 : 0;
}

function mediana(valori: number[]): number {
  if (valori.length === 0) return 0;
  const ordinati = valori.slice().sort((a, b) => a - b);
  const meta = Math.floor(ordinati.length / 2);
  return ordinati.length % 2 ? ordinati[meta] : (ordinati[meta - 1] + ordinati[meta]) / 2;
}

/** L'etichetta di un'opzione: definizione di oggi → istantanea della risposta → il codice. */
function etichettaOpzione(domanda: Domanda, id: string, istantanee: IstantaneaDomanda[][]): string {
  const oggi = (domanda.opzioni ?? []).find((o) => o.id === id);
  if (oggi) return oggi.etichetta;
  for (const ist of istantanee) {
    const d = ist.find((x) => x.id === domanda.id);
    const o = d && d.opzioni ? d.opzioni.find((x) => x.id === id) : undefined;
    if (o) return o.etichetta;
  }
  return id;
}

export function calcolaStatistiche(domande: Domanda[], tutte: RispostaPerStatistiche[]): StatisticheQuestionario {
  const valide = tutte.filter(contaNeiConti);
  const istantanee = valide.map((r) => r.istantanea_domande ?? []).filter((i) => i.length > 0);

  const provenienze: { [p: string]: number } = {};
  const giorni: { [g: string]: number } = {};
  let consenso = 0;
  valide.forEach((r) => {
    const p = etichettaProvenienza(r.provenienza);
    provenienze[p] = (provenienze[p] ?? 0) + 1;
    const g = giornoDi(r.completato_il);
    giorni[g] = (giorni[g] ?? 0) + 1;
    if (r.consenso_richiesto) consenso += 1;
  });

  const perDomanda = domande.map((d) => statisticheDomanda(d, valide, istantanee));

  return {
    totale: valide.length,
    per_provenienza: Object.keys(provenienze)
      .map((p) => ({ provenienza: p, conteggio: provenienze[p] }))
      .sort((a, b) => b.conteggio - a.conteggio),
    per_giorno: Object.keys(giorni)
      .sort()
      .map((g) => ({ giorno: g, conteggio: giorni[g] })),
    consenso_richiesto: consenso,
    domande: perDomanda,
  };
}

function statisticheDomanda(d: Domanda, valide: RispostaPerStatistiche[], istantanee: IstantaneaDomanda[][]): StatisticheDomanda {
  const base: StatisticheDomanda = {
    domanda_id: d.id,
    testo: d.testo,
    nome_breve: d.nome_breve || d.testo,
    tipo: d.tipo,
    risposte: 0,
  };
  const righe: Array<{ response_id: string; quando: string | number; riga: RispostaSalvata }> = [];
  valide.forEach((r) => {
    const riga = r.risposte.find((x) => x.domanda_id === d.id);
    if (riga) righe.push({ response_id: r.id, quando: r.completato_il, riga });
  });
  base.risposte = righe.length;

  const testi: Array<{ response_id: string; testo: string; quando: string | number }> = [];
  righe.forEach(({ response_id, quando, riga }) => {
    if (riga.dettaglio) testi.push({ response_id, testo: riga.dettaglio, quando });
  });

  if (d.tipo === 'scelta_singola' || d.tipo === 'scelta_multipla' || d.tipo === 'si_no' || d.tipo === 'scala_1_10') {
    const conteggi: { [id: string]: number } = {};
    const ordine: string[] = [];
    // Prima le opzioni di oggi, nel loro ordine, anche a zero: un'opzione mai scelta è un'informazione.
    if (d.opzioni) d.opzioni.forEach((o) => { conteggi[o.id] = 0; ordine.push(o.id); });
    if (d.tipo === 'si_no') ['si', 'no'].forEach((id) => { conteggi[id] = 0; ordine.push(id); });
    if (d.tipo === 'scala_1_10') for (let i = 1; i <= 10; i++) { conteggi[String(i)] = 0; ordine.push(String(i)); }
    righe.forEach(({ riga }) => {
      const scelte = Array.isArray(riga.valore) ? riga.valore : [String(riga.valore)];
      scelte.forEach((id) => {
        if (conteggi[id] === undefined) { conteggi[id] = 0; ordine.push(id); }
        conteggi[id] += 1;
      });
    });
    base.opzioni = ordine.map((id) => ({
      id,
      etichetta: d.tipo === 'si_no' ? (id === 'si' ? SI_NO_ITALIANO.si : SI_NO_ITALIANO.no) : d.opzioni ? etichettaOpzione(d, id, istantanee) : id,
      conteggio: conteggi[id],
      percentuale: percentuale(conteggi[id], righe.length),
    }));
  } else if (d.tipo === 'numero') {
    const numeri = righe.map(({ riga }) => riga.valore).filter((v): v is number => typeof v === 'number');
    const somma = numeri.reduce((a, b) => a + b, 0);
    base.numero = {
      risposte: numeri.length,
      somma,
      media: numeri.length ? Math.round((somma / numeri.length) * 10) / 10 : 0,
      mediana: mediana(numeri),
      minimo: numeri.length ? Math.min.apply(null, numeri) : 0,
      massimo: numeri.length ? Math.max.apply(null, numeri) : 0,
      fasce: FASCE_NUMERO.map((f) => ({ etichetta: f.etichetta, conteggio: numeri.filter((n) => n >= f.da && n <= f.a).length })),
    };
  } else {
    righe.forEach(({ response_id, quando, riga }) => {
      if (typeof riga.valore === 'string' && riga.valore) testi.push({ response_id, testo: riga.valore, quando });
    });
  }

  if (testi.length > 0) {
    base.testi = testi
      .sort((a, b) => new Date(b.quando).getTime() - new Date(a.quando).getTime())
      .map(({ response_id, testo }) => ({ response_id, testo }));
  }
  return base;
}

// ─────────────────────────────────────────────────────────────────────────────
// Una risposta in parole (riepilogo dell'email, esportazione, scheda in console)
// ─────────────────────────────────────────────────────────────────────────────

/** Come si scrivono «sì» e «no» nella lingua di chi legge (chi mostra passa le sue parole). */
export interface ParoleSiNo {
  si: string;
  no: string;
}
export const SI_NO_ITALIANO: ParoleSiNo = { si: 'Sì', no: 'No' };

/** Il valore di una risposta come si legge: etichette al posto dei codici, dettaglio fra parentesi. */
export function valoreLeggibile(
  d: Pick<Domanda, 'tipo' | 'opzioni'>,
  valore: ValoreRisposta,
  dettaglio?: string,
  siNo: ParoleSiNo = SI_NO_ITALIANO,
): string {
  const etichetta = (id: string): string => {
    const o = (d.opzioni ?? []).find((x) => x.id === id);
    return o ? o.etichetta : id;
  };
  let testo: string;
  if (Array.isArray(valore)) testo = valore.map(etichetta).join(', ');
  else if (d.tipo === 'scelta_singola') testo = etichetta(String(valore));
  else if (d.tipo === 'si_no') testo = valore === 'si' ? siNo.si : siNo.no;
  else testo = String(valore);
  return dettaglio ? `${testo} (${dettaglio})` : testo;
}

/** Il riepilogo delle risposte: nome breve → valore leggibile, nell'ordine delle domande. */
export function riepilogoRisposte(
  domande: Domanda[],
  risposte: RispostaSalvata[],
  siNo: ParoleSiNo = SI_NO_ITALIANO,
): Array<{ nome: string; valore: string }> {
  const righe: Array<{ nome: string; valore: string }> = [];
  domande.forEach((d) => {
    const r = risposte.find((x) => x.domanda_id === d.id);
    if (!r) return;
    righe.push({ nome: d.nome_breve || d.testo, valore: valoreLeggibile(d, r.valore, r.dettaglio, siNo) });
  });
  return righe;
}
